import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCourses, getSubjects } from '../services/api';
import CourseCard from '../components/CourseCard';
import { CourseCardSkeleton } from '../components/Skeleton';
import './CoursesPage.css';

// ── Egyptian education stages ─────────────────────────────────────────────────
const GRADE_STAGES = [
  {
    label: "رياض الأطفال",
    icon: "🌱",
    grades: ["KG1", "KG2"],
  },
  {
    label: "الابتدائية",
    icon: "📖",
    grades: [
      "الصف الأول الابتدائي",
      "الصف الثاني الابتدائي",
      "الصف الثالث الابتدائي",
      "الصف الرابع الابتدائي",
      "الصف الخامس الابتدائي",
      "الصف السادس الابتدائي",
    ],
  },
  {
    label: "الإعدادية",
    icon: "📐",
    grades: [
      "الصف الأول الإعدادي",
      "الصف الثاني الإعدادي",
      "الصف الثالث الإعدادي",
    ],
  },
  {
    label: "الثانوية العامة",
    icon: "🎓",
    grades: [
      "الصف الأول الثانوي",
      "الصف الثاني الثانوي",
      "الصف الثالث الثانوي",
    ],
  },
  {
    label: "التعليم الفني",
    icon: "🔧",
    grades: ["ثانوي صناعي", "ثانوي تجاري", "ثانوي زراعي", "ثانوي فندقي"],
  },
  {
    label: "الجامعي",
    icon: "🏛️",
    grades: [
      "السنة الأولى جامعة",
      "السنة الثانية جامعة",
      "السنة الثالثة جامعة",
      "السنة الرابعة جامعة",
      "السنة الخامسه جامعة",
    ],
  },
  {
    label: "عام",
    icon: " 📚",
    grades: ["الجميع"],
  },
];

// Flat list for dropdown
const ALL_GRADES = GRADE_STAGES.flatMap(s => s.grades);

export default function CoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses,  setCourses]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [subject,  setSubject]  = useState(searchParams.get('subject') || 'الكل');
  const [grade,    setGrade]    = useState(searchParams.get('grade') || '');        // '' = all grades
  const [search,   setSearch]   = useState(searchParams.get('search') || '');
  const [page,     setPage]     = useState(1);
  const [subjects, setSubjects] = useState(['الكل']);
  const [showGrades, setShowGrades] = useState(false); // mobile toggle
  const limit = 9;

  useEffect(() => {
    getSubjects()
      .then(r => setSubjects(['الكل', ...(r.data.subjects || [])]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit };
    if (subject !== 'الكل') params.subject = subject;
    if (grade) params.grade = grade;
    if (search) params.search = search;

    getCourses(params)
      .then(r => { setCourses(r.data.courses || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subject, grade, page, search]);

  const filtered = courses; // Filtering now happens server-side

  const clearFilters = () => {
    setSubject('الكل');
    setGrade('');
    setPage(1);
    setSearch('');
    setSearchParams({});
  };
  const hasActiveFilter = subject !== 'الكل' || grade !== '' || search !== '';

  // sync search with url
  useEffect(() => {
    const q = searchParams.get('search');
    if (q !== null && q !== search) setSearch(q);
  }, [searchParams]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="courses-page page-enter"
    >

      {/* ── Header ── */}
      <div className="courses-header">
        <div className="courses-header-inner">
          <h1 data-reveal="up" data-delay="100">الكورسات 📚</h1>
          <p data-reveal="up" data-delay="160">تصفح أفضل الكورسات من مدرسين مستقلين محترفين</p>
        </div>
      </div>

      <div className="courses-body">

        {/* ── Filters ── */}
        <div className="courses-filters">

          {/* Search */}
          <div data-reveal="down" data-delay="80">
            <input
              className="form-input search-input"
              placeholder="🔍 ابحث عن كورس أو مدرّس..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Subject pills */}
          <div className="filter-section" data-reveal="fade" data-delay="120">
            <div className="filter-section-label">المادة</div>
            <div className="subject-pills">
              {subjects.map(s => (
                <button
                  key={s}
                  className={`subject-pill ${subject === s ? 'active' : ''}`}
                  onClick={() => { setSubject(s); setPage(1); }}
                >{s}</button>
              ))}
            </div>
          </div>

          {/* Grade filter — grouped by stage */}
          <div className="filter-section" data-reveal="fade" data-delay="160">
            <div className="filter-section-header">
              <div className="filter-section-label">الصف الدراسي</div>
              <button
                className="grade-toggle-btn"
                onClick={() => setShowGrades(v => !v)}
              >
                {showGrades ? '▲ إخفاء' : '▼ عرض الصفوف'}
              </button>
            </div>

            {/* Current grade badge */}
            {grade && (
              <div className="active-grade-badge">
                <span>📌 {grade}</span>
                <button onClick={() => { setGrade(''); setPage(1); }}>✕</button>
              </div>
            )}

            <div className={`grade-stages ${showGrades ? 'open' : ''}`}>
              {GRADE_STAGES.map(stage => (
                <div key={stage.label} className="grade-stage">
                  <div className="grade-stage-label">
                    <span>{stage.icon}</span>
                    <span>{stage.label}</span>
                  </div>
                  <div className="grade-pills">
                    {stage.grades.map(g => (
                      <button
                        key={g}
                        className={`grade-pill ${grade === g ? 'active' : ''}`}
                        onClick={() => {
                          setGrade(prev => prev === g ? '' : g);
                          setPage(1);
                          setShowGrades(false);
                        }}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Clear filters */}
          {hasActiveFilter && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              ✕ مسح الفلاتر
            </button>
          )}
        </div>

        {/* Active filter summary */}
        {hasActiveFilter && (
          <div className="active-filters-bar">
            {subject !== 'الكل' && (
              <span className="filter-chip">
                📚 {subject}
                <button onClick={() => setSubject('الكل')}>✕</button>
              </span>
            )}
            {grade && (
              <span className="filter-chip filter-chip-grade">
                🎓 {grade}
                <button onClick={() => setGrade('')}>✕</button>
              </span>
            )}
          </div>
        )}

        {/* ── Grid ── */}
        {loading ? (
          <div className="courses-grid" style={{ marginTop: '24px' }}>
            {Array(6).fill(0).map((_, i) => <CourseCardSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state page-enter">
            <div style={{ fontSize: 52 }}>📭</div>
            <h3>لا توجد كورسات</h3>
            <p>جرّب تغيير المادة أو الصف الدراسي</p>
            {hasActiveFilter && (
              <button className="btn btn-outline btn-sm" onClick={clearFilters} style={{ marginTop: 8 }}>
                مسح الفلاتر
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="results-info">{total} كورس متاح</div>
            <div className="courses-grid">
              {filtered.map((c, i) => (
                <div
                  key={c.id}
                  className="course-card-anim"
                  style={{ animationDelay: `${(i % 9) * 50}ms` }}
                >
                  <CourseCard course={c} />
                </div>
              ))}
            </div>

            {total > limit && (
              <div className="pagination">
                <button className="btn btn-outline btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>السابق</button>
                <span>صفحة {page} من {Math.ceil(total / limit)}</span>
                <button className="btn btn-outline btn-sm" disabled={page >= Math.ceil(total / limit)} onClick={() => setPage(p => p + 1)}>التالي</button>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}
